import React, { useEffect, useRef } from 'react';

import useBodyStyles from '@pray/shared/hooks/useBodyStyles';
import useEventListener from '@pray/shared/hooks/useEventListener';
import { combineStyles } from '@pray/shared/utils';
import { cn } from '@pray/shared/utils/styles';

import Button from '../Button/Button';
import { Close } from '../Icons/Icons';
import Image from '../Image/Image';

import defaultStyles from './Modal.module.scss';

/**
 * Everything that can hold focus. `[tabindex="-1"]` is excluded on purpose: it
 * is programmatically focusable but not part of the tab ring, so trapping onto
 * it would strand the user on an element Tab cannot leave.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Whether an element is actually reachable.
 *
 * The `hidden` attribute is the easy half. The half that matters in a browser is
 * CSS: a dialog whose first control is `display: none` would otherwise trap
 * focus on something the user cannot see, and every one of these dialogs hides
 * controls with a class rather than the attribute. jsdom computes `display` from
 * inline styles and stylesheets, so this is testable rather than aspirational.
 *
 * The known limit, stated rather than hidden: `getComputedStyle` reports an
 * element's own `display`, not its ancestors', so a focusable buried inside a
 * `display: none` wrapper still reads as visible here. Walking the whole
 * ancestor chain on every Tab is a cost this does not need — `offsetParent`
 * would be the cheap way to do it and jsdom always returns `null` for it, which
 * would make every trap spec in this file vacuous.
 */
const isVisible = (el) => {
  if (el.hasAttribute('hidden')) return false;

  const style = el.ownerDocument?.defaultView?.getComputedStyle(el);
  if (!style) return true;

  return style.display !== 'none' && style.visibility !== 'hidden';
};

const getFocusable = (container) => Array.from(container?.querySelectorAll(FOCUSABLE_SELECTOR) ?? []).filter(isVisible);

export default function Modal({
  children,
  customStyles = {},
  onClose = null,
  onBackdropClick = null,
  isShowCloseButton = true,
  className = '',
  modalClassName = '',
  noPadding = false,
  closeIcon = null,
  type = 'default', // default, large
  heroImage = null,
  /**
   * Id of the element that titles this dialog. Prefer it over `ariaLabel` —
   * a visible heading and the accessible name should be the same string.
   */
  ariaLabelledBy = null,
  /** Accessible name when there is no titling element to point at. */
  ariaLabel = null,
  ...props
}) {
  const modalRef = useRef(null);
  const openerRef = useRef(undefined);
  const styles = combineStyles(defaultStyles, customStyles);

  // Captured during render, not in the effect: React applies `autoFocus` while
  // committing, so by the time any effect runs the "previously focused"
  // element is already a field inside this dialog, and restoring to it on close
  // would put focus on a detached node — i.e. back on <body>.
  if (openerRef.current === undefined) {
    openerRef.current = typeof document === 'undefined' ? null : document.activeElement;
  }

  const handleKeyDown = (event) => {
    const key = event.key?.toLowerCase();
    if (key !== 'escape' && key !== 'tab') return;

    const modal = modalRef.current;
    if (!modal) return;

    // Only the topmost dialog responds. The listener is on `window`, so with two
    // dialogs mounted at once every one of them would otherwise handle the same
    // keypress: Escape would close both, and Tab would have each force focus
    // back to its own first control, which is a fight the user cannot win. DOM
    // order is the stacking order here — a dialog rendered later paints on top.
    const dialogs = document.querySelectorAll('[data-modal]');
    if (dialogs.length && dialogs[dialogs.length - 1] !== modal) return;

    if (key === 'escape') {
      // Optional call, not a bare one. `onClose` defaults to null and dozens of
      // call sites rely on that default, so a bare call turned every Escape
      // keypress in those dialogs into a TypeError.
      onClose?.();
      return;
    }

    // Focus trap. Without it, Tab walks straight out of the dialog and into the
    // page behind it, which is still fully interactive — a screen-reader or
    // keyboard user leaves the dialog without ever being told it closed.
    const focusable = getFocusable(modal);

    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (!modal.contains(active)) {
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Through a ref, because `useEventListener` binds with `[]` deps: it attaches
  // whatever function it is handed on the first render and never rebinds. A
  // handler passed directly would therefore keep that render's `onClose`
  // forever, so a dialog whose parent swaps the callback — a paywall moving
  // between steps, say — would call the one it was mounted with. The arrow below
  // is the function that gets bound; it reads the current handler each time.
  const keyDownRef = useRef(handleKeyDown);
  keyDownRef.current = handleKeyDown;

  useEventListener('keydown', (event) => keyDownRef.current(event));

  useBodyStyles({
    overflow: 'hidden',
  });

  useEffect(() => {
    const modal = modalRef.current;

    // Only pull focus in if it is not already here. Several dialogs mark a
    // field `autoFocus`; React has already honoured it by the time this runs,
    // and moving focus to the close button would undo the consumer's choice.
    if (modal && !modal.contains(document.activeElement)) {
      const [firstFocusable] = getFocusable(modal);
      (firstFocusable ?? modal).focus?.();
    }

    return () => {
      // Restore to whatever opened the dialog. Without this, focus falls back to
      // <body> on close and the next Tab restarts from the top of the page,
      // which for a card rail means losing the user's place in the row.
      openerRef.current?.focus?.();
    };
  }, []);

  const stylesArray = [styles.container, className];
  if (type === 'large') stylesArray.push(styles.large);
  const classes = stylesArray.filter((item) => item).join(' ');

  const modalStyles = [styles.modal, modalClassName];
  const modalClasses = modalStyles.filter((item) => item).join(' ');

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onBackdropClick?.();
    }
  };

  return (
    <div className={classes} onClick={handleBackdropClick} role="none" {...props}>
      <div
        className={modalClasses}
        ref={modalRef}
        data-modal
        role="dialog"
        aria-modal="true"
        // A dialog with no accessible name is an axe violation, and every
        // existing call site predates these props — so there is a fallback
        // rather than a hole. Pass `ariaLabelledBy` and the fallback goes away.
        {...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : { 'aria-label': ariaLabel || 'Dialog' })}
        tabIndex={-1}
      >
        {heroImage && (
          <div data-id="hero-image">
            <Image src={heroImage} alt="Hero" />
          </div>
        )}
        <div className={cn(type !== 'large' && 'p-5 md:px-6 md:py-9')}>
          {isShowCloseButton && onClose && (
            <Button
              className={styles.close}
              data-closebutton
              data-tapeventname="Close"
              aria-label="Close"
              onClick={onClose}
            >
              {closeIcon || <Close />}
            </Button>
          )}
          <div className={noPadding ? defaultStyles.viewportCustom : styles.viewport}>{children}</div>
        </div>
      </div>
    </div>
  );
}
